import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, ArrowUp, ArrowDown, X, Plus } from "lucide-react";
import { api, type ApiResponse } from "@/api/client";
import type { HomepageSettings } from "@ckgo2/shared/types";
import MediaPicker from "@/components/common/MediaPicker";

interface EntityOption {
  id: string;
  label: string;
}

function useEntityOptions(path: string, field: "title" | "name") {
  const { data } = useQuery({
    queryKey: ["homepage-options", path],
    queryFn: () =>
      api.get<ApiResponse<{ data?: Record<string, unknown>[] } | Record<string, unknown>[]>>(
        `${path}?limit=200`,
      ),
  });
  return useMemo<EntityOption[]>(() => {
    const raw = data?.data;
    const list = Array.isArray(raw)
      ? raw
      : ((raw as { data?: Record<string, unknown>[] })?.data ?? []);
    return list.map((r) => ({ id: String(r.id), label: String(r[field] ?? r.id) }));
  }, [data, field]);
}

export default function HomepagePage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["settings", "homepage"],
    queryFn: () => api.get<ApiResponse<HomepageSettings>>("/api/admin/settings/homepage"),
  });

  const trips = useEntityOptions("/api/admin/trips", "title");
  const activities = useEntityOptions("/api/admin/inspirations", "title");
  const destinations = useEntityOptions("/api/admin/destinations", "name");

  const [form, setForm] = useState<HomepageSettings>({
    heroTitle: "",
    heroSubtitle: "",
    heroBackgroundImageId: null,
    monthlyHits: [],
    popularDestinations: [],
  });

  useEffect(() => {
    if (data?.data) {
      setForm({
        heroTitle: data.data.heroTitle ?? "",
        heroSubtitle: data.data.heroSubtitle ?? "",
        heroBackgroundImageId: data.data.heroBackgroundImageId ?? null,
        monthlyHits: data.data.monthlyHits ?? [],
        popularDestinations: data.data.popularDestinations ?? [],
      });
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => api.put("/api/admin/settings/homepage", form),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings", "homepage"] }),
  });

  // Lookups: entity id -> label, for rendering selected lists.
  const tripLabel = useMemo(() => new Map(trips.map((t) => [t.id, t.label])), [trips]);
  const actLabel = useMemo(() => new Map(activities.map((a) => [a.id, a.label])), [activities]);
  const destLabel = useMemo(
    () => new Map(destinations.map((d) => [d.id, d.label])),
    [destinations],
  );

  const move = <T,>(list: T[], i: number, dir: -1 | 1): T[] => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  };

  // Combined options for monthly hits (trips + activities), tagged by type.
  const hitOptions = useMemo(
    () => [
      ...trips.map((t) => ({ type: "trip" as const, id: t.id, label: `Trip: ${t.label}` })),
      ...activities.map((a) => ({
        type: "activity" as const,
        id: a.id,
        label: `Inspiration: ${a.label}`,
      })),
    ],
    [trips, activities],
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Homepage</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Edit the hero, monthly hits, and popular destinations
          </p>
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="btn-primary"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saveMutation.isSuccess ? "Saved" : "Save"}
        </button>
      </div>

      {/* Hero */}
      <section className="card space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Hero</h2>
        <div>
          <label className="label">Title</label>
          <input
            value={form.heroTitle}
            onChange={(e) => setForm((f) => ({ ...f, heroTitle: e.target.value }))}
            className="input"
            maxLength={200}
          />
        </div>
        <div>
          <label className="label">Subtitle</label>
          <input
            value={form.heroSubtitle}
            onChange={(e) => setForm((f) => ({ ...f, heroSubtitle: e.target.value }))}
            className="input"
            maxLength={300}
          />
        </div>
        <div>
          <label className="label">Background image</label>
          <MediaPicker
            mode="single"
            folder="general"
            value={form.heroBackgroundImageId}
            onChange={(id) => setForm((f) => ({ ...f, heroBackgroundImageId: id }))}
          />
        </div>
      </section>

      {/* Monthly hits */}
      <section className="card space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Travel hits of the month
        </h2>
        <p className="text-sm text-gray-500">
          Order matters — first item appears first. Leave empty to keep the default content.
        </p>
        <ul className="space-y-2">
          {form.monthlyHits.map((h, i) => (
            <li
              key={`${h.type}:${h.id}`}
              className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 text-sm dark:border-gray-700"
            >
              <span className="flex-1 truncate">
                {h.type === "trip" ? tripLabel.get(h.id) : actLabel.get(h.id)}{" "}
                <span className="text-xs text-gray-400">({h.type})</span>
              </span>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, monthlyHits: move(f.monthlyHits, i, -1) }))}
              >
                <ArrowUp className="h-4 w-4 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, monthlyHits: move(f.monthlyHits, i, 1) }))}
              >
                <ArrowDown className="h-4 w-4 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    monthlyHits: f.monthlyHits.filter((_, idx) => idx !== i),
                  }))
                }
              >
                <X className="text-error-400 hover:text-error-600 h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
        <AddRefSelect
          options={hitOptions
            .filter((o) => !form.monthlyHits.some((h) => h.type === o.type && h.id === o.id))
            .map((o) => ({ value: `${o.type}:${o.id}`, label: o.label }))}
          onAdd={(value) => {
            const [type, id] = value.split(":") as ["trip" | "activity", string];
            setForm((f) => ({ ...f, monthlyHits: [...f.monthlyHits, { type, id }] }));
          }}
        />
      </section>

      {/* Popular destinations */}
      <section className="card space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Most popular destinations
        </h2>
        <p className="text-sm text-gray-500">
          Order matters. Leave empty to keep the default content.
        </p>
        <ul className="space-y-2">
          {form.popularDestinations.map((d, i) => (
            <li
              key={d.id}
              className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 text-sm dark:border-gray-700"
            >
              <span className="flex-1 truncate">{destLabel.get(d.id) ?? d.id}</span>
              <select
                value={d.size ?? "small"}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    popularDestinations: f.popularDestinations.map((x, idx) =>
                      idx === i ? { ...x, size: e.target.value as "large" | "small" } : x,
                    ),
                  }))
                }
                className="rounded border border-gray-300 px-1.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800"
              >
                <option value="large">large</option>
                <option value="small">small</option>
              </select>
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    popularDestinations: move(f.popularDestinations, i, -1),
                  }))
                }
              >
                <ArrowUp className="h-4 w-4 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({ ...f, popularDestinations: move(f.popularDestinations, i, 1) }))
                }
              >
                <ArrowDown className="h-4 w-4 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    popularDestinations: f.popularDestinations.filter((_, idx) => idx !== i),
                  }))
                }
              >
                <X className="text-error-400 hover:text-error-600 h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
        <AddRefSelect
          options={destinations
            .filter((d) => !form.popularDestinations.some((x) => x.id === d.id))
            .map((d) => ({ value: d.id, label: d.label }))}
          onAdd={(id) =>
            setForm((f) => ({
              ...f,
              popularDestinations: [...f.popularDestinations, { id, size: "small" }],
            }))
          }
        />
      </section>
    </div>
  );
}

/** A "choose entity → add" select that resets after each pick. */
function AddRefSelect({
  options,
  onAdd,
}: {
  options: { value: string; label: string }[];
  onAdd: (value: string) => void;
}) {
  const [value, setValue] = useState("");
  return (
    <div className="flex gap-2">
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="input flex-1 text-sm"
      >
        <option value="">Add…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!value}
        onClick={() => {
          onAdd(value);
          setValue("");
        }}
        className="btn-ghost text-sm"
      >
        <Plus className="h-4 w-4" /> Add
      </button>
    </div>
  );
}
